import React from 'react';
import { Award, Globe, Lightbulb, BookOpen, Target, Smartphone } from 'lucide-react';

const About = () => {
    const colors = {
        primary: '#53354a',
        secondary: '#F5793B',
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-gradient-to-r from-accent2 to-primary py-16 px-8">
                <div className="max-w-6xl mx-auto text-center">
                    <h1 className="text-4xl font-bold mb-4 text-white">About ALE</h1>
                    <p className="text-xl mb-8 max-w-2xl mx-auto text-white">The story behind the future of self-guided tourism</p>
                </div>
            </header>

            {/* Our Story */}
            <section className="py-16 px-8">
                <div className="max-w-6xl mx-auto">
                    <div className="flex flex-col md:flex-row gap-12 items-center">
                        <div className="md:w-1/2">
                            <h2 className="text-3xl font-bold mb-6" style={{ color: colors.primary }}>Our Story</h2>
                            <p className="text-lg mb-4">ALE was born from a simple frustration: why do travelers still have to choose between the depth of a private guide and the freedom of exploring on their own?</p>
                            <p className="text-lg mb-4">Our founder visited a historic city and watched tourists struggling — juggling multiple apps, missing the best stories because they couldn't find a guide in their language, or rushing through places on someone else's schedule.</p>
                            <p className="text-lg mb-4">We built ALE to give every traveler the experience they deserve: a personal AI guide that knows where you are, speaks your language, and lets you explore at your own pace.</p>
                            <blockquote className="border-l-4 pl-4 my-6 italic" style={{ borderColor: colors.secondary }}>
                                "One faces the future with one's past." - Pearl S. Buck
                            </blockquote>
                            <p className="text-lg">Today, ALE is helping travelers around the world discover the hidden stories of the places they visit — independently, personally, and on their own terms.</p>
                        </div>
                        <div className="md:w-1/2 bg-white p-8 rounded-lg shadow-md">
                            <div className="aspect-w-16 aspect-h-9 mb-6 bg-gray-200 rounded-lg flex items-center justify-center">
                                <div className="text-6xl text-center" style={{ color: colors.secondary }}>ALE</div>
                            </div>
                            <h3 className="text-xl font-bold mb-3" style={{ color: colors.primary }}>Our Mission</h3>
                            <p className="mb-4">To give every traveler the freedom of independent exploration with the depth and richness of a personal guide.</p>
                            <h3 className="text-xl font-bold mb-3" style={{ color: colors.primary }}>Our Vision</h3>
                            <p>A world where every place can tell its own story, and every traveler can experience it in a personalized, meaningful way.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Advantage */}
            <section className="py-16 px-8 bg-white">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-3xl font-bold text-center mb-6" style={{ color: colors.primary }}>Our Advantage</h2>
                    <p className="text-xl text-center max-w-4xl mx-auto mb-12 text-gray-600">
                        Where location meets conversation: AI that knows exactly where you are and answers exactly what you ask.
                    </p>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-white p-8 rounded-lg shadow-md text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full" style={{ backgroundColor: colors.secondary }}>
                                <Target className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-4" style={{ color: colors.primary }}>Location Intelligence</h3>
                            <p className="text-gray-600">Pinpoint accuracy through advanced GPS technology delivers relevant information exactly where you stand.</p>
                        </div>

                        <div className="bg-white p-8 rounded-lg shadow-md text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full" style={{ backgroundColor: colors.secondary }}>
                                <BookOpen className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-4" style={{ color: colors.primary }}>Natural Conversation</h3>
                            <p className="text-gray-600">Engages you with dynamic dialogue rather than pre-recorded audio, answering questions in real-time across 50+ languages.</p>
                        </div>

                        <div className="bg-white p-8 rounded-lg shadow-md text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full" style={{ backgroundColor: colors.secondary }}>
                                <Smartphone className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-4" style={{ color: colors.primary }}>Mobile First</h3>
                            <p className="text-gray-600">Download the app and go. Seamless experiences on any smartphone with minimal setup.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="py-16 px-8">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-3xl font-bold text-center mb-12" style={{ color: colors.primary }}>Our Values</h2>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-white p-8 rounded-lg shadow-md text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full" style={{ backgroundColor: colors.secondary }}>
                                <Lightbulb className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-4" style={{ color: colors.primary }}>Innovation</h3>
                            <p className="text-gray-600">We constantly push the boundaries of what's possible in location-based experiences, leveraging cutting-edge AI to transform how people explore the world.</p>
                        </div>

                        <div className="bg-white p-8 rounded-lg shadow-md text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full" style={{ backgroundColor: colors.secondary }}>
                                <Award className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-4" style={{ color: colors.primary }}>Excellence</h3>
                            <p className="text-gray-600">We're committed to delivering exceptional quality in everything we do, from the accuracy of our information to the seamlessness of our user experience.</p>
                        </div>

                        <div className="bg-white p-8 rounded-lg shadow-md text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full" style={{ backgroundColor: colors.secondary }}>
                                <Globe className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-4" style={{ color: colors.primary }}>Inclusivity</h3>
                            <p className="text-gray-600">We believe every traveler should have access to rich stories regardless of language or background. Our multilingual platform breaks down barriers to understanding.</p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default About;